// src/components/sales/pages/salesNotes/SalesNotesView.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Plus,
  RefreshCw,
  Pencil,
  Trash2,
  StickyNote,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import SalesNoteFormModal from "./SalesNoteFormModal";

export default function SalesNotesView({
  loading,
  notes,
  clients,
  clientsMap,
  selectedClientId,
  setSelectedClientId,
  onRefresh,
  onCreate,
  onUpdate,
  onDelete,
  pageSize = 5, // ✅ default 10
}) {
  const { t, i18n } = useTranslation();
  void i18n;

  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState(null);

  // ✅ Pagination state (client-side)
  const [page, setPage] = useState(1);

  const safeTotal = Array.isArray(notes) ? notes.length : 0;
  const size = Math.max(1, Number(pageSize) || 10);
  const totalPages = Math.max(1, Math.ceil(safeTotal / size));
  const safePage = Math.min(Math.max(1, Number(page) || 1), totalPages);

  const startIndex = safeTotal === 0 ? 0 : (safePage - 1) * size + 1;
  const endIndex = safeTotal === 0 ? 0 : Math.min(safePage * size, safeTotal);

  const pagedNotes = useMemo(() => {
    const list = Array.isArray(notes) ? notes : [];
    const start0 = (safePage - 1) * size;
    return list.slice(start0, start0 + size);
  }, [notes, safePage, size]);

  // ✅ reset page when filter changes or data changes
  useEffect(() => {
    setPage(1);
  }, [selectedClientId]);

  useEffect(() => {
    setPage(1);
  }, [safeTotal]);

  const onPageChange = (p) => {
    const next = Math.min(Math.max(1, Number(p) || 1), totalPages);
    setPage(next);
  };

  const pageButtons = useMemo(() => {
    const pages = totalPages;
    const cur = safePage;

    // show up to 7 buttons
    const maxBtns = 7;
    if (pages <= maxBtns) return Array.from({ length: pages }, (_, i) => i + 1);

    const half = Math.floor(maxBtns / 2);
    let start = Math.max(1, cur - half);
    let end = start + maxBtns - 1;

    if (end > pages) {
      end = pages;
      start = end - maxBtns + 1;
    }

    const arr = [];
    for (let i = start; i <= end; i++) arr.push(i);
    return arr;
  }, [safePage, totalPages]);

  const headers = useMemo(
    () => [
      t("sales.salesNotes.table.client"),
      t("sales.salesNotes.table.noteContent"),
      t("sales.salesNotes.table.outcome"),
      t("sales.salesNotes.table.actions"),
    ],
    [t]
  );

  const openCreate = () => {
    setEditing(null);
    setOpenModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    setEditing(null);
  };

  const clientLabel = (clientId) => {
    const c = clientsMap?.get?.(String(clientId));
    if (!c) return t("sales.salesNotes.clientNumber", { id: clientId });
    const name = c.companyName || "-";
    const person = `${c.firstName || ""} ${c.secondName || ""}`.trim();
    return person ? `${name} — ${person}` : name;
  };

  return (
    <div className="text-white">
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-xl bg-white/10 border border-white/20 backdrop-blur-xl">
            <StickyNote className="w-6 h-6 text-pink-200" />
          </div>
          <div>
            <h2 className="text-xl font-bold">{t("sales.salesNotes.title")}</h2>
            <p className="text-xs text-white/70">{t("sales.salesNotes.subtitle")}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={openCreate}
            className="px-4 py-2 rounded-xl bg-purple-600/80 hover:bg-purple-700 text-white flex items-center gap-2 backdrop-blur-lg disabled:opacity-50"
            disabled={loading}
          >
            <Plus className="w-5 h-5" />
            {t("sales.salesNotes.actions.add")}
          </button>

          <button
            onClick={onRefresh}
            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white flex items-center gap-2 backdrop-blur-lg disabled:opacity-50"
            disabled={loading}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
            {t("common.refresh")}
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="mb-5 backdrop-blur-xl bg-white/10 rounded-2xl border border-white/20 shadow-[0_0_30px_rgba(255,0,255,0.15)] p-4">
        <div className="grid md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="text-sm text-gray-200">
              {t("sales.salesNotes.filter.label")}
            </label>

            <select
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              className={selectCls}
              disabled={loading}
            >
              <option value="" className="text-black">
                {t("sales.salesNotes.filter.all")}
              </option>
              {(clients || []).map((c) => (
                <option key={c.id} value={c.id} className="text-black">
                  {c.companyName || t("sales.salesNotes.clientNumber", { id: c.id })}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2 text-sm text-gray-200 text-right">
            {t("sales.salesNotes.showingCount", { count: safeTotal })}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="backdrop-blur-xl bg-white/10 rounded-2xl shadow-[0_0_40px_rgba(255,0,255,0.20)] border border-white/20 overflow-hidden">
        <div className="p-6 bg-gradient-to-r from-pink-500/20 to-purple-600/30 backdrop-blur-xl flex items-center justify-between">
          <h3 className="text-lg font-bold">{t("sales.salesNotes.listTitle")}</h3>
          <span className="text-sm text-gray-200">
            {t("sales.salesNotes.totalCount", { total: safeTotal })}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-white">
            <thead className="bg-white/10">
              <tr>
                {headers.map((h) => (
                  <th key={h} className="px-4 py-3 text-right text-sm font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-white/10">
              {pagedNotes.map((n) => (
                <tr key={n.id} className="hover:bg-white/10 transition">
                  <td className="px-4 py-3 text-right">
                    <div className="font-semibold">{clientLabel(n.clientId)}</div>
                    <div className="text-xs text-gray-300">
                      {t("sales.salesNotes.clientIdLabel", { id: n.clientId })}
                    </div>
                  </td>

                  <td className="px-4 py-3 text-right">
                    <div className="text-sm whitespace-pre-wrap break-words max-w-[520px]">
                      {n.noteContent || "-"}
                    </div>
                  </td>

                  <td className="px-4 py-3 text-right">
                    <span className="px-2 py-1 rounded text-xs bg-white/10 border border-white/20">
                      {n.outcome || "-"}
                    </span>
                  </td>

                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => openEdit(n)}
                        className="px-3 py-1.5 rounded-lg bg-pink-600/80 hover:bg-pink-700 text-white text-xs backdrop-blur-lg flex items-center gap-1 disabled:opacity-50"
                        disabled={loading}
                      >
                        <Pencil className="w-4 h-4" />
                        {t("common.edit")}
                      </button>

                      <button
                        onClick={() => onDelete?.(n.id)}
                        className="px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-400/30 text-red-100 text-xs backdrop-blur-lg flex items-center gap-1 disabled:opacity-50"
                        disabled={loading}
                      >
                        <Trash2 className="w-4 h-4" />
                        {t("common.delete")}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {!loading && safeTotal === 0 && (
                <tr>
                  <td className="px-4 py-10 text-center text-gray-200" colSpan={4}>
                    {t("sales.salesNotes.empty")}
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td className="px-4 py-10 text-center text-gray-200" colSpan={4}>
                    {t("common.loading")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ✅ Pagination Footer (i18n) */}
        <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between px-4 py-4 border-t border-white/10 bg-white/5">
          <div className="text-xs text-white/70">
            {safeTotal === 0
              ? t("sales.salesNotes.pagination.none")
              : t("sales.salesNotes.pagination.showing", {
                  start: startIndex,
                  end: endIndex,
                  total: safeTotal,
                })}
          </div>

          <div className="flex items-center gap-2 justify-end">
            <button
              type="button"
              onClick={() => onPageChange(safePage - 1)}
              disabled={loading || safePage <= 1}
              className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white disabled:opacity-50 flex items-center gap-2"
              title={t("sales.salesNotes.pagination.prev")}
            >
              <ChevronRight className="w-4 h-4" />
              {t("sales.salesNotes.pagination.prev")}
            </button>

            <div className="flex items-center gap-1">
              {pageButtons.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => onPageChange(p)}
                  disabled={loading}
                  className={[
                    "min-w-[40px] px-3 py-2 rounded-xl border text-sm",
                    p === safePage
                      ? "bg-purple-600/60 border-purple-300/30 text-white"
                      : "bg-white/10 hover:bg-white/15 border-white/20 text-white",
                    loading ? "opacity-50" : "",
                  ].join(" ")}
                  aria-current={p === safePage ? "page" : undefined}
                  title={t("sales.salesNotes.pagination.pageOf", { page: p, pages: totalPages })}
                >
                  {p}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => onPageChange(safePage + 1)}
              disabled={loading || safePage >= totalPages}
              className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white disabled:opacity-50 flex items-center gap-2"
              title={t("sales.salesNotes.pagination.next")}
            >
              {t("sales.salesNotes.pagination.next")}
              <ChevronLeft className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {openModal && (
        <SalesNoteFormModal
          clients={clients || []}
          initialData={editing}
          onClose={closeModal}
          onSubmit={async (payload) => {
            if (editing?.id) {
              const ok = await onUpdate?.(editing.id, payload);
              if (ok) closeModal();
            } else {
              const ok = await onCreate?.(payload);
              if (ok) closeModal();
            }
          }}
        />
      )}
    </div>
  );
}

const selectCls =
  "mt-2 w-full px-3 py-2 bg-white/10 text-white border border-white/20 rounded-lg outline-none focus:ring-2 focus:ring-pink-400/30";
